export type DerivationConditionMode = 'all' | 'any';

export interface EditableConditionRow {
  ui_id: string;
  field: string;
  op: string;
  value: any;
  negated?: boolean;
}

export interface EditableParamRow {
  ui_id: string;
  key: string;
  value: any;
}

export interface EditableDerivationRule {
  ui_id: string;
  target_key?: string;
  rule_key: string;
  priority: number;
  is_active: boolean;
  required_inputs: string[];
  condition_mode: DerivationConditionMode;
  conditions: EditableConditionRow[];
  expression: string;
  params_entries: EditableParamRow[];
  formula_key?: string | null;
  formula_name?: string | null;
  formula_version?: string | null;
  output_unit?: string | null;
  round_digits?: number | null;
  prompt_hint?: string | null;
  reference_note?: string | null;
}

const DEFAULT_OP = 'eq';

export const CONDITION_OPERATOR_OPTIONS = [
  { label: '=', value: 'eq' },
  { label: '!=', value: '!=' },
  { label: '>', value: '>' },
  { label: '>=', value: '>=' },
  { label: '<', value: '<' },
  { label: '<=', value: '<=' },
  { label: 'in', value: 'in' },
  { label: 'not in', value: 'not_in' },
  { label: 'exists', value: 'exists' },
  { label: 'missing', value: 'missing' },
];

export type DerivationConditionValueType = 'number' | 'select' | 'boolean' | 'text';

export interface DerivationConditionFieldConfig {
  field: string;
  label: string;
  category: string;
  value_type: DerivationConditionValueType;
  unit?: string;
  operators?: string[];
  options?: Array<{ label: string; value: any }>;
  default_op?: string;
  default_value?: any;
}

const NUMERIC_OPERATORS = ['eq', '!=', '>', '>=', '<', '<=', 'exists', 'missing'];
const SELECT_OPERATORS = ['eq', '!=', 'in', 'not_in', 'exists', 'missing'];
const BOOLEAN_OPERATORS = ['eq', '!=', 'exists', 'missing'];
const TEXT_OPERATORS = ['eq', '!=', 'in', 'not_in', 'exists', 'missing'];

const POPULATION_OPTIONS = [
  { value: 'neonate', label: '新生儿' },
  { value: 'pediatric', label: '儿童' },
  { value: 'adult', label: '成人' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: '男性' },
  { value: 'female', label: '女性' },
];

const BOOLEAN_OPTIONS = [
  { value: 'true', label: '是' },
  { value: 'false', label: '否' },
];

export const DERIVATION_CONDITION_FIELD_GROUPS: Array<{
  label: string;
  options: DerivationConditionFieldConfig[];
}> = [
  {
    label: '人群',
    options: [
      {
        field: 'population_group',
        label: '人群',
        category: '人群',
        value_type: 'select',
        operators: SELECT_OPERATORS,
        options: POPULATION_OPTIONS,
        default_op: 'eq',
        default_value: 'neonate',
      },
    ],
  },
  {
    label: '年龄',
    options: [
      {
        field: 'age_years',
        label: '年龄(岁)',
        category: '年龄',
        value_type: 'number',
        unit: '岁',
        operators: NUMERIC_OPERATORS,
        default_op: '<',
        default_value: 18,
      },
      {
        field: 'age_pna',
        label: '出生后日龄(天)',
        category: '年龄',
        value_type: 'number',
        unit: '天',
        operators: NUMERIC_OPERATORS,
        default_op: '<=',
        default_value: 28,
      },
      {
        field: 'age_pma',
        label: '纠正胎龄PMA(周)',
        category: '年龄',
        value_type: 'number',
        unit: '周',
        operators: NUMERIC_OPERATORS,
        default_op: '>=',
        default_value: '',
      },
      {
        field: 'gestational_age',
        label: '胎龄(周)',
        category: '年龄',
        value_type: 'number',
        unit: '周',
        operators: NUMERIC_OPERATORS,
        default_op: '>=',
        default_value: '',
      },
    ],
  },
  {
    label: '基础信息',
    options: [
      {
        field: 'gender',
        label: '性别',
        category: '基础信息',
        value_type: 'select',
        operators: SELECT_OPERATORS,
        options: GENDER_OPTIONS,
        default_op: 'eq',
        default_value: 'male',
      },
      {
        field: 'height',
        label: '身高(cm)',
        category: '基础信息',
        value_type: 'number',
        unit: 'cm',
        operators: NUMERIC_OPERATORS,
        default_op: 'exists',
        default_value: '',
      },
      {
        field: 'weight',
        label: '体重(kg)',
        category: '基础信息',
        value_type: 'number',
        unit: 'kg',
        operators: NUMERIC_OPERATORS,
        default_op: 'exists',
        default_value: '',
      },
    ],
  },
  {
    label: '实验室',
    options: [
      {
        field: 'scr_mg_dl',
        label: 'SCr(mg/dL)',
        category: '实验室',
        value_type: 'number',
        unit: 'mg/dL',
        operators: NUMERIC_OPERATORS,
        default_op: '<=',
        default_value: '',
      },
      {
        field: 'scr',
        label: 'SCr原始值',
        category: '实验室',
        value_type: 'number',
        operators: NUMERIC_OPERATORS,
        default_op: 'exists',
        default_value: '',
      },
      {
        field: 'scr_unit',
        label: 'SCr单位',
        category: '实验室',
        value_type: 'select',
        operators: SELECT_OPERATORS,
        options: [
          { value: 'mg/dL', label: 'mg/dL' },
          { value: 'umol/L', label: 'umol/L' },
          { value: 'μmol/L', label: 'μmol/L' },
        ],
        default_op: 'eq',
        default_value: 'mg/dL',
      },
    ],
  },
  {
    label: '临床状态',
    options: [
      {
        field: 'ventilation',
        label: '机械通气',
        category: '临床状态',
        value_type: 'boolean',
        operators: BOOLEAN_OPERATORS,
        options: BOOLEAN_OPTIONS,
        default_op: 'eq',
        default_value: true,
      },
      {
        field: 'inotropes',
        label: '正性肌力药',
        category: '临床状态',
        value_type: 'boolean',
        operators: BOOLEAN_OPERATORS,
        options: BOOLEAN_OPTIONS,
        default_op: 'eq',
        default_value: true,
      },
      {
        field: 'ecmo',
        label: 'ECMO',
        category: '临床状态',
        value_type: 'boolean',
        operators: BOOLEAN_OPERATORS,
        options: BOOLEAN_OPTIONS,
        default_op: 'eq',
        default_value: true,
      },
      {
        field: 'CRRT',
        label: 'CRRT',
        category: '临床状态',
        value_type: 'boolean',
        operators: BOOLEAN_OPERATORS,
        options: BOOLEAN_OPTIONS,
        default_op: 'eq',
        default_value: true,
      },
    ],
  },
];

const CONDITION_FIELD_CONFIGS = DERIVATION_CONDITION_FIELD_GROUPS
  .flatMap((group) => group.options)
  .reduce<Record<string, DerivationConditionFieldConfig>>((acc, option) => {
    acc[option.field] = option;
    return acc;
  }, {});

export const DERIVATION_CONDITION_PRESETS = [
  { key: 'population_group', label: '人群条件', field: 'population_group' },
  { key: 'age_years', label: '年龄条件', field: 'age_years' },
  { key: 'gender', label: '性别条件', field: 'gender' },
  { key: 'scr_mg_dl', label: 'SCr 条件', field: 'scr_mg_dl' },
  { key: 'height', label: '身高存在', field: 'height' },
  { key: 'weight', label: '体重存在', field: 'weight' },
  { key: 'custom', label: '自定义字段', field: '' },
];

export function getConditionFieldConfig(field?: string): DerivationConditionFieldConfig {
  const key = String(field || '').trim();
  return CONDITION_FIELD_CONFIGS[key] || {
    field: key,
    label: key || '自定义字段',
    category: '自定义',
    value_type: 'text',
    operators: TEXT_OPERATORS,
    default_op: DEFAULT_OP,
    default_value: '',
  };
}

export function getConditionOperatorOptions(field?: string) {
  const allowed = getConditionFieldConfig(field).operators || TEXT_OPERATORS;
  return CONDITION_OPERATOR_OPTIONS.filter((option) => allowed.includes(option.value));
}

function defaultValueForConfig(config: DerivationConditionFieldConfig): any {
  if (config.default_value !== undefined) return config.default_value;
  if (config.default_op === 'exists' || config.default_op === 'missing') return '';
  if (config.value_type === 'boolean') return 'true';
  if (config.value_type === 'select') return config.options?.[0]?.value ?? '';
  return '';
}

function formatConditionValue(field: string, value: any): string {
  const config = getConditionFieldConfig(field);
  const formatOne = (item: any) => {
    const option = config.options?.find((entry) => String(entry.value) === String(item));
    if (option) return option.label;
    if (item === true || item === 'true') return '是';
    if (item === false || item === 'false') return '否';
    return String(item ?? '');
  };
  return Array.isArray(value) ? value.map(formatOne).join(', ') : formatOne(value);
}

export function formatConditionRow(row: EditableConditionRow): string {
  const config = getConditionFieldConfig(row.field);
  const opLabel = CONDITION_OPERATOR_OPTIONS.find((option) => option.value === row.op)?.label || row.op;
  const prefix = row.negated ? '非 ' : '';
  if (row.op === 'exists') return `${prefix}${config.label} 存在`;
  if (row.op === 'missing') return `${prefix}${config.label} 缺失`;
  return `${prefix}${config.label} ${opLabel} ${formatConditionValue(row.field, row.value)}`;
}

export function formatRuleConditionSummary(rule: EditableDerivationRule, maxItems = 4): string[] {
  const items = (rule.conditions || []).map(formatConditionRow);
  if (items.length <= maxItems) return items;
  return [...items.slice(0, maxItems), `+${items.length - maxItems} 条`];
}

function makeUiId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function newEditorRule(targetKey?: string, index = 0): EditableDerivationRule {
  return {
    ui_id: makeUiId('rule'),
    target_key: targetKey,
    rule_key: '',
    priority: (index + 1) * 10,
    is_active: true,
    required_inputs: [],
    condition_mode: 'all',
    conditions: [],
    expression: '',
    params_entries: [],
    formula_key: '',
    formula_name: '',
    formula_version: '',
    output_unit: '',
    round_digits: null,
    prompt_hint: '',
    reference_note: '',
  };
}

export function newConditionRow(field?: string): EditableConditionRow {
  const config = getConditionFieldConfig(field);
  return {
    ui_id: makeUiId('condition'),
    field: field || '',
    op: config.default_op || DEFAULT_OP,
    value: defaultValueForConfig(config),
    negated: false,
  };
}

export function newParamRow(): EditableParamRow {
  return {
    ui_id: makeUiId('param'),
    key: '',
    value: '',
  };
}

function coerceScalar(value: any): any {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (raw === '') return '';
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'null') return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : value;
}

function normalizeListValue(value: any): any {
  if (Array.isArray(value)) return value.map(coerceScalar);
  if (typeof value === 'string' && value.includes(',')) {
    return value.split(',').map((item) => coerceScalar(item.trim())).filter((item) => item !== '');
  }
  return coerceScalar(value);
}

function isLeafCondition(value: any): boolean {
  return value && typeof value === 'object' && typeof value.field === 'string';
}

function conditionToRows(condition: any, negated = false): EditableConditionRow[] {
  if (!condition || typeof condition !== 'object') return [];
  if (condition.not) {
    return conditionToRows(condition.not, !negated);
  }
  if (isLeafCondition(condition)) {
    return [{
      ui_id: makeUiId('condition'),
      field: condition.field || '',
      op: condition.op || DEFAULT_OP,
      value: condition.value ?? '',
      negated,
    }];
  }
  return [];
}

function resolveConditionMode(condition: any): DerivationConditionMode {
  if (condition?.any) return 'any';
  return 'all';
}

function resolveConditionItems(condition: any): any[] {
  if (Array.isArray(condition?.any)) return condition.any;
  if (Array.isArray(condition?.all)) return condition.all;
  if (isLeafCondition(condition) || condition?.not) return [condition];
  return [];
}

export function ruleToEditorRule(rule: any, index = 0): EditableDerivationRule {
  const conditionLogic = rule?.condition_logic || {};
  const conditionMode = resolveConditionMode(conditionLogic);
  const conditions = resolveConditionItems(conditionLogic).flatMap((item) => conditionToRows(item));
  const params = rule?.params && typeof rule.params === 'object' ? rule.params : {};

  return {
    ui_id: makeUiId('rule'),
    target_key: rule?.target_key,
    rule_key: rule?.rule_key || '',
    priority: Number(rule?.priority ?? ((index + 1) * 10)),
    is_active: rule?.is_active !== false,
    required_inputs: Array.isArray(rule?.required_inputs) ? rule.required_inputs : [],
    condition_mode: conditionMode,
    conditions,
    expression: rule?.expression || '',
    params_entries: Object.entries(params).map(([key, value]) => ({
      ui_id: makeUiId('param'),
      key,
      value,
    })),
    formula_key: rule?.formula_key || '',
    formula_name: rule?.formula_name || '',
    formula_version: rule?.formula_version || '',
    output_unit: rule?.output_unit || '',
    round_digits: rule?.round_digits ?? null,
    prompt_hint: rule?.prompt_hint || '',
    reference_note: rule?.reference_note || '',
  };
}

function conditionRowToApi(row: EditableConditionRow): any | null {
  const field = String(row.field || '').trim();
  const op = String(row.op || DEFAULT_OP).trim();
  if (!field || !op) return null;
  const leaf: Record<string, any> = { field, op };
  if (op !== 'exists' && op !== 'missing') {
    leaf.value = op === 'in' || op === 'not_in'
      ? normalizeListValue(row.value)
      : coerceScalar(row.value);
  }
  return row.negated ? { not: leaf } : leaf;
}

function buildConditionLogic(rule: EditableDerivationRule): any {
  const conditions = (rule.conditions || [])
    .map(conditionRowToApi)
    .filter(Boolean);
  if (conditions.length === 0) return {};
  return { [rule.condition_mode || 'all']: conditions };
}

function buildParams(rule: EditableDerivationRule): Record<string, any> {
  return (rule.params_entries || []).reduce<Record<string, any>>((acc, row) => {
    const key = String(row.key || '').trim();
    if (!key) return acc;
    acc[key] = coerceScalar(row.value);
    return acc;
  }, {});
}

function emptyToNull(value: any): any {
  return value === '' ? null : value;
}

export function editorRulesToApiRules(
  rules: EditableDerivationRule[],
  targetKey: string,
): any[] {
  return (rules || [])
    .filter((rule) => String(rule.rule_key || '').trim() || String(rule.expression || '').trim())
    .map((rule, index) => ({
      target_key: rule.target_key || targetKey,
      rule_key: String(rule.rule_key || '').trim(),
      priority: Number(rule.priority ?? ((index + 1) * 10)),
      is_active: rule.is_active !== false,
      required_inputs: rule.required_inputs || [],
      condition_logic: buildConditionLogic(rule),
      expression: String(rule.expression || '').trim(),
      params: buildParams(rule),
      formula_key: emptyToNull(rule.formula_key),
      formula_name: emptyToNull(rule.formula_name),
      formula_version: emptyToNull(rule.formula_version),
      output_unit: emptyToNull(rule.output_unit),
      round_digits: rule.round_digits ?? null,
      prompt_hint: emptyToNull(rule.prompt_hint),
      reference_note: emptyToNull(rule.reference_note),
    }));
}
